import { useCallback, useMemo } from 'react';

import type { UserTypeEnum } from '@snowit/models';
import type { AuthJWT } from '@snowit/types/auth';
import type { PermissionModel, RoleModel } from '@snowit/types/permissions';
import type { UserModel } from '@snowit/types/users';
import type PERMISSIONS from '@snowit/utilities/permissions';
import type { UserCredential } from 'firebase/auth';

import FirebaseSDKFactory from '@snowit/firebase/FirebaseSDKFactory';

import { AUTH_TENANT_STORAGE_KEY } from '../useTenants/utils';
import { useAuthStore } from './store';

const getType = (obj: AuthJWT): UserTypeEnum => {
  if (obj.admin) return 'ADMIN';
  if (obj.partner) return 'PARTNER';
  return 'USER';
};
export default () => {
  const firebase = FirebaseSDKFactory();
  const authUser = useAuthStore((state) => state.user);
  const logged = useAuthStore((state) => state.logged);
  const checked = useAuthStore((state) => state.checked);
  const jwt = useAuthStore((state) => state.jwt);
  const db = useAuthStore((state) => state.db);
  const allRoles = useAuthStore((state) => state.roles);
  const allPermissions = useAuthStore((state) => state.permissions);
  const setDb = useAuthStore((state) => state.setDb);
  const setRoles = useAuthStore((state) => state.setRoles);
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const type: UserTypeEnum = getType(jwt);

  const roles: RoleModel[] = useMemo(() => {
    if (type === 'ADMIN') return allRoles;
    return allRoles.filter((role) => (jwt.roles ?? []).includes(role.uid));
  }, [allRoles, jwt.roles, type]);

  const permissions: PERMISSIONS[] = useMemo(() => {
    return roles.reduce((memo, role) => {
      return Array.from(new Set([...memo, ...(role.permissions as PERMISSIONS[])]));
    }, [] as PERMISSIONS[]);
  }, [roles]);

  const isTenantManager = useCallback(
    (tenant?: string): boolean => {
      if (!tenant || type === 'USER') return false;
      return (db?.tenantsManager ?? []).some((uid) => uid === 'all' || uid === tenant);
    },
    [db?.tenantsManager, type],
  );

  const getAuthUser = async (): Promise<void> => {
    const res = await firebase.doc<UserModel>({ collectionName: 'users', id: authUser.uid });
    setDb(res);
  };

  const signIn = useCallback(
    async (email: string, password: string, authTenant?: string): Promise<UserCredential> => {
      return firebase.signIn(email, password, authTenant);
    },
    [],
  );

  const signOut = useCallback(async () => {
    const savedAuthTenant = window.localStorage.getItem(AUTH_TENANT_STORAGE_KEY);
    window.localStorage.clear();
    if (savedAuthTenant !== null) {
      window.localStorage.setItem(AUTH_TENANT_STORAGE_KEY, savedAuthTenant);
    }
    return firebase.signOut();
  }, []);

  const sendSignInLink = useCallback(async (email: string, url: string) => {
    return firebase.sendSignInLinkToEmail(email, url);
  }, []);

  const sendResetPassword = useCallback(async (email: string, url: string) => {
    return firebase.resetPassword(email, url);
  }, []);

  const hasCommonAuthTenant = useCallback(
    (target?: Pick<UserModel, 'authTenants'>) => {
      const own = db?.authTenants ?? [];
      return (target?.authTenants ?? []).some((tenant) => own.includes(tenant));
    },
    [db?.authTenants],
  );

  /**
   * Check if user has at least one permission
   * @param {string[]} params
   */
  const hasPermission = useCallback(
    (...permKeys: PERMISSIONS[]) => {
      if (jwt.admin) return true;
      return permKeys.some((key) => permissions.includes(key));
    },
    [permissions, jwt.admin],
  );

  /**
   * Check if user has all permissions
   * @param {string[]} params
   */
  const hasEveryPermission = useCallback(
    (...permKeys: PERMISSIONS[]) => {
      if (jwt.admin) return true;
      return permKeys.every((key) => permissions.includes(key));
    },
    [permissions, jwt.admin],
  );

  const updateRoles = (roles: RoleModel[]) => {
    setRoles(roles);
  };

  const updatePermissions = (permissions: PermissionModel[]) => {
    setPermissions(permissions);
  };

  const displayName = useMemo(() => {
    return (
      authUser.displayName ||
      db?.displayName ||
      [db?.name, db?.surname].filter(Boolean).join(' ') ||
      authUser.email ||
      db?.email
    );
  }, [authUser.displayName, authUser.email, db?.displayName, db?.email, db?.name, db?.surname]);

  return {
    authUID: authUser.uid,
    authDisplayName: displayName,
    authType: type,
    /** @deprecated use authType === 'ADMIN' */
    admin: type === 'ADMIN',
    /** @deprecated use authType === 'PARTNER' */
    partner: type === 'PARTNER',
    uid: authUser.uid,
    email: authUser.email,
    displayName: authUser.displayName || displayName,
    authUser,
    logged,
    checked,
    user: db,
    roles,
    permissions,
    allRoles,
    allPermissions,
    /** @deprecated use authType */
    type,
    claims: jwt,
    getAuthUser,
    updateRoles,
    updatePermissions,
    signOut,
    signIn,
    hasPermission,
    hasEveryPermission,
    hasCommonAuthTenant,
    isTenantManager,
    sendSignInLink,
    sendResetPassword,
  };
};
